/**
 * PDFPreviewOverlay - In-app PDF preview using Mozilla's pdf.js via react-pdf.
 *
 * Renders PDFs using the react-pdf library, which wraps pdfjs-dist.
 * This is more reliable than <embed> for Electron apps (see GitHub issues
 * electron#11065, electron#33094, electron#33519).
 *
 * The PDF is loaded from a Uint8Array (via IPC) and rendered to canvas.
 * The pdf.js worker handles decoding and rendering in a background thread.
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { FileText, ChevronLeft, ChevronRight } from 'lucide-react'
import { PreviewOverlay } from './PreviewOverlay'
import { CopyButton } from './CopyButton'
import { ItemNavigator } from './ItemNavigator'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import type { FullscreenOverlayBaseHeaderTranslations } from './FullscreenOverlayBaseHeader'

type PdfModule = typeof import('react-pdf')

interface PreviewItem {
  src: string
  label?: string
}

export interface PDFPreviewOverlayProps {
  isOpen: boolean
  onClose: () => void
  /** Absolute file path for the PDF */
  filePath: string
  /** Optional multi-item navigation */
  items?: PreviewItem[]
  /** Initial selected item index for multi-item mode */
  initialIndex?: number
  /** Async loader that returns PDF data as Uint8Array */
  loadPdfData: (path: string) => Promise<Uint8Array>
  theme?: 'light' | 'dark'
  /** Optional localized strings */
  translations?: {
    previousPage?: string
    nextPage?: string
    copyPath?: string
    loadFailed?: string
    loading?: string
    rendering?: string
  }
  /** Optional localized strings for overlay header/menu */
  headerTranslations?: FullscreenOverlayBaseHeaderTranslations
}

export function PDFPreviewOverlay({
  isOpen,
  onClose,
  filePath,
  items,
  initialIndex = 0,
  loadPdfData,
  theme = 'light',
  translations,
  headerTranslations,
}: PDFPreviewOverlayProps) {
  const resolvedItems = useMemo<PreviewItem[]>(
    () => (items && items.length > 0 ? items : [{ src: filePath }]),
    [items, filePath]
  )
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.min(Math.max(initialIndex, 0), Math.max(0, resolvedItems.length - 1))
  )
  const activeFilePath = resolvedItems[activeIndex]?.src ?? filePath

  const t = {
    previousPage: translations?.previousPage ?? 'Previous page',
    nextPage: translations?.nextPage ?? 'Next page',
    copyPath: translations?.copyPath ?? 'Copy path',
    loadFailed: translations?.loadFailed ?? 'Load Failed',
    loading: translations?.loading ?? 'Loading PDF...',
    rendering: translations?.rendering ?? 'Rendering...',
  }
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [pdfModule, setPdfModule] = useState<PdfModule | null>(null)
  const [isModuleLoading, setIsModuleLoading] = useState(false)
  const Document = pdfModule?.Document
  const Page = pdfModule?.Page

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(0, resolvedItems.length - 1)))
  }, [resolvedItems.length])

  useEffect(() => {
    if (!isOpen) return
    setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(0, resolvedItems.length - 1)))
  }, [isOpen, initialIndex, resolvedItems.length])

  // Lazy-load react-pdf + worker when overlay opens
  useEffect(() => {
    if (!isOpen || pdfModule) return

    let cancelled = false
    setIsModuleLoading(true)

    Promise.all([
      import('react-pdf'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ])
      .then(([reactPdf, worker]) => {
        if (cancelled) return
        const workerUrl = (worker as { default: string }).default
        reactPdf.pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
        setPdfModule(reactPdf)
        setIsModuleLoading(false)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t.loadFailed)
          setIsModuleLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [isOpen, pdfModule, t.loadFailed])

  // Load PDF data when overlay opens
  useEffect(() => {
    if (!isOpen || !activeFilePath) return

    let cancelled = false
    setIsLoading(true)
    setError(null)
    setPdfData(null)
    setPageNumber(1)
    setNumPages(0)

    loadPdfData(activeFilePath)
      .then((data) => {
        if (!cancelled) {
          setPdfData(data)
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t.loadFailed)
          setIsLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [isOpen, activeFilePath, loadPdfData])

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }, [])

  const onDocumentLoadError = useCallback((error: Error) => {
    setError(`${t.loadFailed}: ${error.message}`)
  }, [t.loadFailed])

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1))
  }, [])

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(numPages, prev + 1))
  }, [numPages])

  // Memoize file object to prevent unnecessary re-renders (react-pdf uses === equality)
  const fileObj = useMemo(() =>
    pdfData ? { data: pdfData } : null,
    [pdfData]
  )

  // Header actions: page navigation + copy button
  const headerActions = (
    <div className="flex items-center gap-2">
      <ItemNavigator items={resolvedItems} activeIndex={activeIndex} onSelect={setActiveIndex} size="md" />
      {numPages > 0 && (
        <>
          <button
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            className="p-1 rounded hover:bg-foreground/5 disabled:opacity-30 disabled:cursor-not-allowed"
            title={t.previousPage}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-muted-foreground min-w-[4rem] text-center">
            {pageNumber} / {numPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            className="p-1 rounded hover:bg-foreground/5 disabled:opacity-30 disabled:cursor-not-allowed"
            title={t.nextPage}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="w-px h-4 bg-foreground/10 mx-1" />
        </>
      )}
      <CopyButton content={activeFilePath} title={t.copyPath} />
    </div>
  )

  return (
    <PreviewOverlay
      isOpen={isOpen}
      onClose={onClose}
      theme={theme}
      typeBadge={{
        icon: FileText,
        label: 'PDF',
        variant: 'orange',
      }}
      filePath={activeFilePath}
      error={error ? { label: t.loadFailed, message: error } : undefined}
      headerActions={headerActions}
      headerTranslations={headerTranslations}
    >
      <div className="h-full flex flex-col items-center justify-center overflow-auto">
        {(isModuleLoading || isLoading) && (
          <div className="text-muted-foreground text-sm">{t.loading}</div>
        )}
        {fileObj && Document && Page && (
          <Document
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={<div className="text-muted-foreground text-sm">{t.rendering}</div>}
          >
            <Page
              pageNumber={pageNumber}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              className="pdf-page"
            />
          </Document>
        )}
      </div>
    </PreviewOverlay>
  )
}
